/* Shade navigation and review. The existing studio owns selections and commerce. */
(() => {
  if (window.KaliaShadeExperience) return;

  const selector = '.kps[data-experience="calm-tech"]';
  const instances = new WeakMap();

  class KaliaShadeExperience {
    constructor(studio) {
      this.studio = studio;
      this.controller = new AbortController();
      this.configuration = studio.getConfiguration?.() || null;
      this.state = studio.purchaseState || {};
      this.noun = studio.family === 'venetian' ? 'blind' : 'shade';
      this.simple = studio.dataset.presentation === 'simple';
      const options = { signal: this.controller.signal };

      studio.addEventListener('kalia:product-configuration-change', event => {
        this.configuration = event.detail || studio.getConfiguration?.() || null;
        this.state = studio.purchaseState || {};
        this.renderChoices();
        this.renderState();
      }, options);

      studio.addEventListener('kalia:product-price-state', event => {
        this.configuration = studio.getConfiguration?.() || this.configuration;
        this.state = event.detail || studio.purchaseState || {};
        this.renderState();
      }, options);

      studio.querySelectorAll('[data-flow-target]').forEach(button => {
        button.addEventListener('click', () => this.goTo(button.dataset.flowTarget), options);
      });

      studio.querySelector('[data-mobile-action]')?.addEventListener('click', () => {
        if (this.state.busy) return;
        this.configuration = studio.getConfiguration?.() || this.configuration;
        if (this.configuration?.valid) this.goTo('review');
        else this.focusFirstMissing();
      }, options);

      studio.querySelector('[data-live-preview]')?.addEventListener('click', () => {
        const button = studio.querySelector('[data-view-button="illustration"]');
        if (!button || button.hidden || button.disabled) return;
        button.click();
        this.focus(button, studio.querySelector('.kps-visual-panel') || button);
      }, options);

      studio.addEventListener('toggle', () => this.numberChoices(), { ...options, capture: true });

      if (this.simple) this.setupAttention();
      this.renderChoices();
      this.renderState();
      studio.dataset.experienceReady = 'true';
      if (this.noun === 'blind') {
        const firstStep = studio.querySelector('[data-flow-target="1"]');
        if (firstStep?.lastChild?.nodeType === 3) firstStep.lastChild.textContent = 'Finish';
        this.text('[data-review-heading]', 'Review your blind');
        this.text('.kps-from-price > span:last-child', 'base price per blind');
      }
      if (!studio.controlField) {
        const control = studio.querySelector('[data-studio-control]');
        if (control && !control.querySelector('.kps-fixed-fact')) {
          const fact = document.createElement('p');
          fact.className = 'kps-fixed-fact';
          fact.textContent = studio.config?.studio?.fixedProperties?.Operation || 'Motorized operation. Choose the compatible motor and control options below.';
          control.append(fact);
        }
      }
    }

    text(selector, value) {
      this.studio.querySelectorAll(selector).forEach(element => {
        if (element.textContent !== value) element.textContent = value;
      });
    }

    setupAttention() {
      const studio = this.studio;
      const options = { signal: this.controller.signal };
      this.flowNav = studio.querySelector('.kps-flow-nav');
      this.mobileSummary = studio.querySelector('.kps-mobile-summary');
      this.primaryAction = studio.querySelector('[data-studio-add]');
      const priceCard = studio.querySelector('.kps-price-card');
      if (priceCard) {
        this.nextDetail = priceCard.querySelector('[data-next-detail]') || document.createElement('button');
        this.nextDetail.type = 'button';
        this.nextDetail.className = 'kps-next-detail';
        this.nextDetail.dataset.nextDetail = '';
        this.nextDetail.hidden = true;
        if (!this.nextDetail.parentElement) priceCard.append(this.nextDetail);
        this.nextDetail.addEventListener('click', () => {
          if (!this.state.busy) this.focusFirstMissing();
        }, options);
      }
      this.flowSections = [
        ['1', studio.querySelector('[data-studio-panel="1"]')],
        ['2', studio.querySelector('[data-studio-panel="2"]')],
        ['review', studio.querySelector('[data-studio-panel="3"]')]
      ].filter(([, element]) => element);
      if (this.flowNav) {
        this.flowNav.hidden = false;
        const details = this.flowNav.querySelector('[data-flow-target="3"]');
        if (details) {
          details.hidden = true;
          details.removeAttribute('aria-current');
        }
        const size = this.flowNav.querySelector('[data-flow-target="2"]');
        if (size?.lastChild?.nodeType === 3) size.lastChild.textContent = 'Size';
        const reviewNumber = this.flowNav.querySelector('[data-flow-target="review"] > span');
        if (reviewNumber) reviewNumber.textContent = '03';
      }
      const queue = () => this.queueAttention();
      window.addEventListener('scroll', queue, { ...options, passive: true });
      window.addEventListener('resize', queue, options);
      window.visualViewport?.addEventListener('resize', queue, options);
      window.visualViewport?.addEventListener('scroll', queue, { ...options, passive: true });
      studio.addEventListener('focusin', queue, options);
      studio.addEventListener('focusout', queue, options);
      studio.addEventListener('toggle', queue, { ...options, capture: true });
      studio.addEventListener('kalia:product-configuration-change', queue, options);
      const targets = [...this.flowSections.map(([, element]) => element), this.primaryAction, studio].filter(Boolean);
      if (window.IntersectionObserver) {
        this.attentionObserver = new window.IntersectionObserver(queue, { threshold: [0, 0.01, 1] });
        targets.forEach(element => this.attentionObserver.observe(element));
      }
      if (window.ResizeObserver) {
        this.attentionResize = new window.ResizeObserver(queue);
        this.attentionResize.observe(studio);
        if (this.flowNav) this.attentionResize.observe(this.flowNav);
      }
      this.updateAttention();
    }

    queueAttention() {
      if (this.attentionFrame || this.controller.signal.aborted) return;
      this.attentionFrame = window.requestAnimationFrame(() => {
        this.attentionFrame = 0;
        this.updateAttention();
      });
    }

    updateAttention() {
      if (this.controller.signal.aborted) return;
      const viewport = window.visualViewport;
      const top = viewport?.offsetTop || 0;
      const bottom = top + (viewport?.height || window.innerHeight);
      const boundary = Math.max(top, this.flowNav?.getBoundingClientRect().bottom || top) + 16;
      let active = this.flowSections?.[0]?.[0];
      this.flowSections?.forEach(([step, element]) => {
        // Anchor scrolling stops at the panel's CSS margin, which may exceed the sticky nav.
        const margin = parseFloat(window.getComputedStyle?.(element)?.scrollMarginTop) || 0;
        if (this.isVisible(element) && element.getBoundingClientRect().top <= Math.max(boundary, top + margin + 16)) active = step;
      });
      this.studio.querySelectorAll('[data-flow-target]').forEach(button => {
        if (!button.hidden && button.dataset.flowTarget === active) button.setAttribute('aria-current', 'step');
        else button.removeAttribute('aria-current');
      });
      if (!this.mobileSummary) return;
      const studioRect = this.studio.getBoundingClientRect();
      const actionRect = this.primaryAction?.getBoundingClientRect();
      const actionVisible = actionRect && this.isVisible(this.primaryAction) && actionRect.width > 0 && actionRect.bottom > top && actionRect.top < bottom;
      const focused = document.activeElement;
      const entering = focused && this.studio.contains(focused) && focused.matches('input:not([type="radio"]):not([type="checkbox"]):not([type="button"]):not([type="submit"]), textarea, select, [contenteditable="true"]');
      const keyboard = viewport && window.innerHeight - viewport.height > 120;
      this.mobileSummary.hidden = Boolean(actionVisible || entering || keyboard || studioRect.bottom <= top || studioRect.top >= bottom);
    }

    isVisible(element) {
      if (!element || element.closest('[hidden]')) return false;
      for (let details = element.closest('details'); details; details = details.parentElement?.closest('details')) {
        if (!details.open && !details.querySelector(':scope > summary')?.contains(element)) return false;
      }
      return true;
    }

    numberChoices() {
      const form = this.studio.querySelector('[data-studio-form]');
      if (!form) return;
      let number = 0;
      form.querySelectorAll('fieldset > legend, [data-measurement-group] h3').forEach(heading => {
        const fieldset = heading.closest('fieldset');
        const sourceField = !fieldset || fieldset.hasAttribute('data-field-id') || Boolean(fieldset.closest('[data-studio-native-options]'));
        const current = Array.from(heading.children).find(child => child.classList.contains('kps-option-number'));
        if (!sourceField || !this.isVisible(heading)) {
          current?.remove();
          return;
        }
        const badge = current || document.createElement('span');
        badge.className = 'kps-option-number';
        badge.setAttribute('aria-hidden', 'true');
        const label = String(++number).padStart(2, '0');
        if (badge.textContent !== label) badge.textContent = label;
        if (!current) heading.prepend(badge);
      });
    }

    renderChoices() {
      this.numberChoices();
      this.renderReview();
      this.renderFit();
    }

    renderReview() {
      const target = this.studio.querySelector('[data-review-specs]');
      if (!target) return;
      const entries = Object.entries(this.configuration?.properties || {}).filter(([key, value]) => (
        key.trim() && !key.startsWith('_') && !key.startsWith('Factory ') && key !== 'Customer measurement unit' && typeof value === 'string' && value.trim()
      ));
      // Native fabric options can share a title with a separate hardware choice.
      // Keep the order properties intact while recovering the selected fabric
      // from the same radio widgets that the studio renders and reconciles.
      this.studio.nativeWidgets?.forEach((widget, name) => {
        const checked = widget.inputs?.find(item => item.input?.checked)?.input;
        const value = checked?.value ?? this.studio.nativeChoices?.[name];
        if (typeof name !== 'string' || !name.trim() || name.startsWith('_') || typeof value !== 'string' || !value.trim()) return;
        if (entries.some(([key, existing]) => key === name && existing === value)) return;
        const conflict = entries.some(([key]) => key === name);
        const title = conflict ? `${name} · ${this.studio.family === 'venetian' ? 'finish' : 'fabric'}` : name;
        entries.push([title, value]);
      });
      const usedTitles = new Map();
      this.studio.widgets?.forEach((widget, fieldId) => {
        if (!this.studio.fieldVisible(fieldId)) return;
        const field = this.studio.fieldMap.get(fieldId);
        const selected = this.studio.selectedOption(field);
        if (!field || !selected) return;
        const title = this.studio.displayTitle(field.title);
        const count = (usedTitles.get(title) || 0) + 1;
        usedTitles.set(title, count);
        const value = this.studio.displayTitle(selected.label);
        if (count > 1 || entries.some(([key, existing]) => key === title && existing !== value)) {
          if (!entries.some(([key, existing]) => key.startsWith(title) && existing === value)) entries.push([`${title} · option ${count}`, value]);
        }
      });
      if (Number.isInteger(this.configuration?.quantity)) entries.push(['Quantity', String(this.configuration.quantity)]);
      // A current, visible Selected receipt owns only the native rows it actually renders.
      // Keep the full review for old markup, incomplete receipts or a stale accessory label.
      const receiptPairs = [];
      const receipts = this.studio.querySelectorAll?.('[data-selected-options]') || [];
      const expected = receipts.length ? this.studio.operationReceipt?.(this.configuration)?.rows || [] : [];
      receipts.forEach(panel => {
        if (!this.isVisible(panel)) return;
        panel.querySelectorAll('[data-selected-operation] > [data-selection-field]').forEach(row => {
          const item = expected.find(item => item.id === row.dataset.selectionField && !item.pending);
          if (!item || row.dataset.selectionPending === 'true' || !this.isVisible(row) ||
            row.querySelector('dt')?.textContent !== item.title || row.querySelector('dd')?.firstChild?.textContent !== item.label) return;
          const field = this.studio.fieldMap.get(item.id), option = this.studio.selectedOption(field);
          if (option) receiptPairs.push([item.title, this.studio.displayTitle(option.label)]);
        });
      });
      const visibleEntries = entries.filter(([key, value]) => !receiptPairs.some(([title, selected]) => selected === value && (
        key === title || key.startsWith(`${title} · option `) && /^[1-9]\d*$/.test(key.slice(`${title} · option `.length))
      )));
      const signature = JSON.stringify(visibleEntries);
      if (signature === this.reviewSignature) return;
      this.reviewSignature = signature;
      const fragment = document.createDocumentFragment();
      visibleEntries.forEach(([key, value]) => {
        const row = document.createElement('div');
        const term = document.createElement('dt');
        const description = document.createElement('dd');
        term.textContent = key;
        description.textContent = value;
        row.append(term, description);
        fragment.append(row);
      });
      target.replaceChildren(fragment);
    }

    renderFit() {
      const target = this.studio.querySelector('[data-fit-feedback]');
      const group = this.studio.querySelector('[data-measurement-group]');
      if (!target || !this.isVisible(group)) return;
      const errors = this.configuration?.errors || [];
      const error = errors.find(item => item.field === 'width' || item.field === 'height');
      const width = this.studio.form?.elements.namedItem('width')?.value?.trim();
      const height = this.studio.form?.elements.namedItem('height')?.value?.trim();
      let text = 'Add width and height to check the available size range.';
      let state = 'empty';
      if (width || height) {
        text = error?.message || 'Measurement entries accepted for the selected options.';
        state = error ? 'incomplete' : 'valid';
      }
      if (target.textContent !== text) target.textContent = text;
      target.dataset.state = state;
    }

    renderState() {
      const configuration = this.configuration;
      const errors = configuration?.errors || [];
      const complete = [1, 2, 3].filter(step => configuration && !errors.some(error => Number(error.step) === step));
      const firstInvalid = [1, 2, 3].find(step => errors.some(error => Number(error.step) === step)) || 1;
      const active = configuration?.valid ? 'review' : String(firstInvalid);

      this.studio.querySelectorAll('[data-flow-target]').forEach(button => {
        const step = button.dataset.flowTarget;
        button.dataset.complete = String(step === 'review' ? Boolean(configuration?.valid && this.state.ready) : complete.includes(Number(step)));
        // Simple navigation follows the visible section; validity still owns completion.
        if (!this.simple) {
          if (step === active) button.setAttribute('aria-current', 'step');
          else button.removeAttribute('aria-current');
        }
      });

      this.text('[data-design-progress]', `${complete.length} of 3 steps complete`);
      this.studio.querySelectorAll('[data-progress-bar]').forEach(bar => {
        bar.style.width = `${complete.length / 3 * 100}%`;
      });

      const busy = Boolean(this.state.busy);
      const ready = Boolean(configuration?.valid && this.state.ready);
      const needsReview = Boolean(configuration?.valid && !ready && this.state.message && !/^Checking current price/i.test(this.state.message));
      const status = busy ? 'Adding…' : ready ? 'Ready to add' : needsReview ? 'Price needs review' : configuration?.valid ? 'Checking your price' : 'Choose your details';
      const statusState = busy ? 'busy' : ready ? 'ready' : needsReview ? 'unavailable' : configuration?.valid ? 'checking' : 'incomplete';
      this.text('[data-design-status]', status);
      this.studio.querySelectorAll('[data-design-status]').forEach(element => {
        element.dataset.state = statusState;
      });

      const price = ready && Number.isSafeInteger(this.state.totalCents) ? this.studio.money(this.state.totalCents) : '—';
      this.text('[data-mobile-total]', price);
      const nextDetail = this.simple ? this.nextDetailLabel() : '';
      this.text('[data-mobile-action]', configuration?.valid ? `Review your ${this.noun || 'shade'}` : nextDetail || `Complete your ${this.noun || 'shade'}`);
      this.studio.querySelectorAll('[data-mobile-action]').forEach(button => {
        button.disabled = busy;
      });
      if (this.nextDetail) {
        this.nextDetail.hidden = !nextDetail || busy;
        this.nextDetail.disabled = busy;
        this.nextDetail.textContent = nextDetail ? `Next: ${nextDetail} →` : '';
      }
      if (this.simple) this.queueAttention();
    }

    nextDetailLabel() {
      if (!this.configuration || this.configuration.valid) return '';
      const error = this.configuration.errors?.[0];
      if (!error) return '';
      if (error.field === 'width' || error.field === 'height') {
        const value = this.studio.form?.elements.namedItem(error.field)?.value;
        const currentLabel = this.studio.querySelector(`[data-${error.field}-label]`)?.textContent;
        const label = typeof currentLabel === 'string' ? currentLabel.replace(/[\u0000-\u001f\u007f]/g, '').replace(/\s+/g, ' ').trim().slice(0, 80).toLowerCase() : '';
        return `${typeof value === 'string' && value.trim() ? 'Check' : 'Enter'} ${label || error.field}`;
      }
      if (error.fieldId) {
        if (this.studio.fieldVisible && !this.studio.fieldVisible(error.fieldId)) return '';
        const field = this.studio.fieldMap?.get(error.fieldId);
        const title = this.studio.displayTitle?.(field?.title);
        if (typeof title !== 'string' || !title.trim()) return '';
        const label = title.replace(/[\u0000-\u001f\u007f]/g, '').trim().slice(0, 72).toLowerCase();
        return `${/^Choose\b/i.test(error.message || '') ? 'Choose' : 'Check'} ${label}`;
      }
      return Number(error.step) === 1 ? 'Choose fabric and color' : '';
    }

    goTo(target) {
      const heading = target === 'review'
        ? this.studio.querySelector('[data-review-heading]')
        : ['1', '2', '3'].includes(String(target))
          ? this.studio.querySelector(`[data-studio-panel="${target}"] h2`)
          : null;
      if (heading) this.focus(heading, target === 'review' ? heading.closest('.kps-review') || heading : heading.closest('[data-studio-panel]') || heading);
    }

    focusFirstMissing() {
      const error = this.configuration?.errors?.[0];
      if (!error) {
        this.goTo('1');
        return;
      }
      const notice = this.studio.querySelector('[data-studio-error]');
      if (notice && notice.textContent !== error.message) notice.textContent = error.message;
      let input = error.field ? this.studio.form?.elements.namedItem(error.field) : null;
      if (!input && error.fieldId) {
        const fieldset = this.studio.widgets?.get(error.fieldId)?.fieldset;
        input = fieldset?.querySelector('input:not([disabled]), select:not([disabled])') || fieldset?.querySelector('legend');
      }
      if (input instanceof Element) {
        this.focus(input, input.closest('fieldset, [data-measurement-group]') || input);
      } else this.goTo(String(error.step || 1));
    }

    focus(element, scrollTarget = element) {
      for (let details = element.closest('details'); details; details = details.parentElement?.closest('details')) details.open = true;
      if (!element.matches('button, input, select, textarea, a[href], [tabindex]')) element.tabIndex = -1;
      element.focus({ preventScroll: true });
      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      scrollTarget.scrollIntoView({ block: 'start', behavior: reducedMotion ? 'auto' : 'smooth' });
    }

    disconnect() {
      this.controller.abort();
      this.attentionObserver?.disconnect();
      this.attentionResize?.disconnect();
      if (this.attentionFrame) window.cancelAnimationFrame(this.attentionFrame);
      this.attentionFrame = 0;
    }
  }

  const attach = studio => {
    if (!studio?.matches?.(selector) || !studio.ready || instances.has(studio)) return;
    instances.set(studio, new KaliaShadeExperience(studio));
  };

  window.KaliaShadeExperience = KaliaShadeExperience;
  document.querySelectorAll(selector).forEach(attach);
  document.addEventListener('kalia:product-studio-ready', event => attach(event.target));
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll(selector).forEach(attach));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll(selector).forEach(studio => {
      instances.get(studio)?.disconnect();
      instances.delete(studio);
    });
  });
})();
